import { ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

import BetaBadge from 'components/betaBadge/BetaBadge';

type TabItem<T extends string> = {
  title: string;
  shortTitle?: string;
  type: T;
  colorVariant?: 'default' | 'orange';
  isBeta?: boolean;
  betaText?: ReactNode;
};

type TabMenuProps<T extends string> = {
  items: TabItem<T>[];
  activeTab: T;
  onTabClick: (type: T) => void;
  variant?: 'default' | 'staking';
};

const containerStyles = {
  default:
    'tw-grid tw-auto-rows-[40px] tw-grid-cols-2 tw-gap-[10px] lg:tw-flex lg:tw-auto-rows-auto lg:tw-grid-cols-none lg:tw-gap-[50px]',
  staking:
    'tw-flex tw-justify-center lg:tw-justify-start lg:tw-gap-[40px] max-[424px]:tw-overflow-x-auto max-[424px]:tw-overflow-y-hidden max-[424px]:tw-scrollbar-hide max-[424px]:tw-pb-[2px]',
};

const stakingTabsWrapperStyles = 'tw-relative tw-inline-flex tw-gap-[0px] lg:tw-gap-[40px]';

const tabBaseStyles = {
  default:
    'tw-relative tw-flex tw-h-[40px] tw-w-full tw-min-w-0 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-[10px] tw-font-jost tw-text-[16px] tw-leading-normal tw-text-white lg:tw-h-[60px] lg:tw-w-auto lg:tw-max-w-none lg:tw-rounded-none lg:tw-bg-transparent lg:tw-leading-[89.2px] lg:tw-min-w-[125px] lg:tw-text-[20px]',
  staking:
    'tw-relative tw-flex tw-h-[36px] tw-w-[90px] tw-flex-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-bg-transparent tw-font-jost tw-text-[12px] tw-font-bold tw-uppercase tw-leading-none tw-text-white lg:tw-h-[60px] lg:tw-w-[200px] lg:tw-text-[18px] xl:tw-text-[24px]',
};

const STAKING_INDICATOR = {
  mobile: { width: 90, gap: 0 },
  desktop: { width: 200, gap: 40 },
};

const INDICATOR_TRANSITION = 'left 300ms cubic-bezier(0.4, 0, 0.2, 1), background-color 300ms ease';

const getIndicatorStyle = (activeIndex: number, size: { width: number; gap: number }, color: string) => ({
  width: size.width,
  left: activeIndex * (size.width + size.gap),
  backgroundColor: color,
  transition: INDICATOR_TRANSITION,
});

export const TabMenu = <T extends string>({ items, activeTab, onTabClick, variant = 'default' }: TabMenuProps<T>) => {
  const isOddCount = items.length % 2 !== 0;
  const activeIndex = items.findIndex((item) => item.type === activeTab);
  const activeItem = items[activeIndex];
  const activeColor = activeItem?.colorVariant === 'orange' ? '#FF9730' : '#2D5EFF';

  const renderTabs = () =>
    items.map((item, index) => {
      const isActive = activeTab === item.type;
      const isLastOdd = isOddCount && index === items.length - 1;
      const isVip = item.colorVariant === 'orange';

      return (
        <div
          key={item.type}
          onClick={() => onTabClick(item.type)}
          className={twMerge(
            tabBaseStyles[variant],
            variant === 'default' &&
              (isActive ? (isVip ? 'tw-bg-[#FF9730]' : 'tw-bg-[#2D5EFF]') : 'tw-bg-[#314272]'),
            variant === 'default' &&
              isLastOdd &&
              'tw-col-span-2 lg:tw-col-span-1 lg:tw-w-auto lg:tw-justify-self-auto'
          )}
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          <span
            className={
              variant === 'default'
                ? 'tw-inline-flex tw-w-full tw-items-center tw-justify-center tw-gap-1.5 tw-text-center lg:tw-w-auto'
                : ''
            }
          >
            {item.shortTitle ? (
              <>
                <span className="lg:tw-hidden">{item.title}</span>
                <span className="tw-hidden lg:tw-inline min-[1405px]:tw-hidden">{item.shortTitle}</span>
                <span className="tw-hidden min-[1405px]:tw-inline">{item.title}</span>
              </>
            ) : (
              item.title
            )}
            {item.isBeta && variant === 'default' && (
              <BetaBadge text={item.betaText} className="tw-ml-0" />
            )}
          </span>
          {variant === 'default' && isActive && (
            <div
              className="tw-absolute tw-bottom-[-3px] tw-left-0 tw-right-0 tw-hidden tw-h-[5px] tw-w-full lg:tw-block"
              style={{ backgroundColor: activeColor }}
            />
          )}
        </div>
      );
    });

  const renderStakingIndicator = () => (
    <>
      <div
        className="tw-absolute tw-bottom-[-3px] tw-h-[5px] lg:tw-hidden"
        style={getIndicatorStyle(activeIndex, STAKING_INDICATOR.mobile, activeColor)}
      />
      <div
        className="tw-absolute tw-bottom-[-3px] tw-hidden tw-h-[5px] lg:tw-block"
        style={getIndicatorStyle(activeIndex, STAKING_INDICATOR.desktop, activeColor)}
      />
    </>
  );

  return (
    <div className="tw-w-full lg:tw-w-auto">
      <div className={twMerge('tw-relative', containerStyles[variant])}>
        {variant === 'staking' ? (
          <div className={stakingTabsWrapperStyles}>
            {renderTabs()}
            {renderStakingIndicator()}
          </div>
        ) : (
          renderTabs()
        )}
      </div>
      <div className={twMerge('tw-h-[1px] tw-bg-[#2D5EFF]', variant === 'default' && 'tw-hidden lg:tw-block')} />
    </div>
  );
};
